import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Sparkles, AlertTriangle, BarChart3, Building2, Target,
    CheckSquare, Presentation, Zap, Search, Shield,
    Plus, Trash2, Edit3, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import AdminLayout from './AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SKILL_CATEGORIES = [
    { value: 'general', label: 'General' },
    { value: 'pipeline', label: 'Pipeline' },
    { value: 'accounts', label: 'Companies' },
    { value: 'deals', label: 'Deals' },
    { value: 'activities', label: 'Activities' },
    { value: 'coaching', label: 'Coaching' },
];

const SKILL_ICONS = [
    'sparkles', 'alert-triangle', 'bar-chart-3', 'building-2', 'target',
    'check-square', 'presentation', 'zap', 'search', 'shield',
];

const iconMap = {
    'sparkles': Sparkles, 'alert-triangle': AlertTriangle, 'bar-chart-3': BarChart3,
    'building-2': Building2, 'target': Target, 'check-square': CheckSquare,
    'presentation': Presentation, 'zap': Zap, 'search': Search, 'shield': Shield,
};

const categoryColors = {
    pipeline: 'bg-blue-50 text-blue-700 border-blue-200',
    accounts: 'bg-amber-50 text-amber-700 border-amber-200',
    deals: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    activities: 'bg-violet-50 text-violet-700 border-violet-200',
    coaching: 'bg-rose-50 text-rose-700 border-rose-200',
    general: 'bg-stone-50 text-stone-700 border-stone-200',
};

function AISkillsTab() {
    const { getAuthHeader } = useAuth();
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);
    const [form, setForm] = useState({
        name: '', description: '', prompt_template: '',
        category: 'general', icon: 'sparkles', is_active: true,
    });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const res = await axios.get(`${API}/admin/skills`, { headers: getAuthHeader() });
            setSkills(res.data);
        } catch { toast.error('Failed to load skills'); }
        finally { setLoading(false); }
    };

    const resetForm = () => setForm({ name: '', description: '', prompt_template: '', category: 'general', icon: 'sparkles', is_active: true });

    const openCreate = () => { resetForm(); setEditingSkill(null); setShowModal(true); };

    const openEdit = (skill) => {
        setEditingSkill(skill);
        setForm({
            name: skill.name, description: skill.description,
            prompt_template: skill.prompt_template,
            category: skill.category || 'general',
            icon: skill.icon || 'sparkles',
            is_active: skill.is_active !== false,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.prompt_template.trim()) return toast.error('Name and prompt template are required');
        try {
            if (editingSkill) {
                await axios.put(`${API}/admin/skills/${editingSkill.skill_id}`, form, { headers: getAuthHeader() });
                toast.success('Skill updated');
            } else {
                await axios.post(`${API}/admin/skills`, form, { headers: getAuthHeader() });
                toast.success('Skill created');
            }
            setShowModal(false);
            loadData();
        } catch { toast.error('Failed to save skill'); }
    };

    const handleDelete = async (skillId) => {
        if (!window.confirm('Delete this skill?')) return;
        try {
            await axios.delete(`${API}/admin/skills/${skillId}`, { headers: getAuthHeader() });
            toast.success('Skill deleted');
            loadData();
        } catch { toast.error('Failed to delete skill'); }
    };

    const toggleActive = async (skill) => {
        try {
            await axios.put(`${API}/admin/skills/${skill.skill_id}`, { is_active: !skill.is_active }, { headers: getAuthHeader() });
            loadData();
        } catch { toast.error('Failed to toggle skill'); }
    };

    if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-stone-700 border-t-transparent rounded-full animate-spin" /></div>;

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-stone-900">AI Skills</h2>
                    <p className="text-sm text-stone-500">Create prompt templates that salespeople can use with the AI Assistant</p>
                </div>
                <Button onClick={openCreate} className="bg-stone-900 hover:bg-stone-800" data-testid="create-skill-btn">
                    <Plus className="w-4 h-4 mr-2" />Create Skill
                </Button>
            </div>

            {skills.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-xl">
                    <Sparkles className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-2">No skills yet</h3>
                    <p className="text-stone-500 mb-4">Create skills that will appear as suggestions in the AI Assistant</p>
                    <Button onClick={openCreate}>Create Your First Skill</Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {skills.map(skill => {
                        const IconComp = iconMap[skill.icon] || Sparkles;
                        const catColor = categoryColors[skill.category] || categoryColors.general;
                        return (
                            <div key={skill.skill_id} className={`bg-white border rounded-xl p-5 transition-all ${skill.is_active ? 'border-stone-200 hover:shadow-md' : 'border-stone-100 opacity-60'}`} data-testid={`skill-card-${skill.skill_id}`}>
                                <div className="flex items-start gap-4">
                                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${catColor.split(' ')[0]}`}>
                                        <IconComp className={`w-5 h-5 ${catColor.split(' ')[1]}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <h3 className="font-semibold text-stone-900">{skill.name}</h3>
                                            <span className={`text-xs px-2 py-0.5 rounded-full border ${catColor}`}>{skill.category}</span>
                                            {!skill.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-400">Inactive</span>}
                                        </div>
                                        <p className="text-sm text-stone-500">{skill.description}</p>
                                        <p className="text-xs text-stone-400 mt-1.5 font-mono bg-stone-50 rounded-md px-2 py-1 truncate">
                                            {skill.prompt_template}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <button onClick={() => toggleActive(skill)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors" title={skill.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-skill-${skill.skill_id}`}>
                                            {skill.is_active ?
                                                <ToggleRight className="w-5 h-5 text-emerald-500" /> :
                                                <ToggleLeft className="w-5 h-5 text-stone-400" />}
                                        </button>
                                        <button onClick={() => openEdit(skill)} className="p-2 hover:bg-stone-100 rounded-lg text-stone-400 hover:text-stone-600" data-testid={`edit-skill-${skill.skill_id}`}>
                                            <Edit3 className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => handleDelete(skill.skill_id)} className="p-2 hover:bg-red-50 rounded-lg text-stone-400 hover:text-red-500" data-testid={`delete-skill-${skill.skill_id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create/Edit Skill Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingSkill ? 'Edit Skill' : 'Create Skill'}</DialogTitle>
                        <DialogDescription>
                            {editingSkill ? 'Update the skill configuration' : 'Create a new AI skill that salespeople can use'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-3">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <Label>Skill Name *</Label>
                                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Pipeline Risk Review" className="mt-1" data-testid="skill-name-input" />
                            </div>
                            <div className="col-span-2">
                                <Label>Description</Label>
                                <Input value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Short description shown to salespeople" className="mt-1" />
                            </div>
                        </div>
                        <div>
                            <Label>Prompt Template *</Label>
                            <Textarea value={form.prompt_template} onChange={(e) => setForm(prev => ({ ...prev, prompt_template: e.target.value }))}
                                placeholder="The actual prompt sent to the AI when this skill is used. Reference CRM data naturally — the AI has access to the full pipeline, accounts, activities, etc."
                                className="mt-1 font-mono text-sm" rows={4} data-testid="skill-prompt-input" />
                            <p className="text-xs text-stone-400 mt-1">The AI has access to real-time CRM data (deals, accounts, activities, contacts) and will use it to answer this prompt.</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Category</Label>
                                <Select value={form.category} onValueChange={(v) => setForm(prev => ({ ...prev, category: v }))}>
                                    <SelectTrigger className="mt-1" data-testid="skill-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {SKILL_CATEGORIES.map(c => (
                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Icon</Label>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {SKILL_ICONS.map(ic => {
                                        const IC = iconMap[ic] || Sparkles;
                                        return (
                                            <button key={ic} onClick={() => setForm(prev => ({ ...prev, icon: ic }))}
                                                className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${form.icon === ic ? 'border-stone-400 bg-stone-100 text-stone-700' : 'border-stone-200 text-stone-400 hover:text-stone-600'}`}>
                                                <IC className="w-4 h-4" />
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={form.is_active} onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))} />
                            <Label className="cursor-pointer">Active — visible in AI Assistant</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                        <Button onClick={handleSave} className="bg-stone-900 hover:bg-stone-800" data-testid="save-skill-btn">
                            {editingSkill ? 'Save Changes' : 'Create Skill'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function AdminAISkillsPage() {
    return (
        <AdminLayout title="AI Skills" description="Create prompt templates for the AI Assistant">
            <AISkillsTab />
        </AdminLayout>
    );
}
